import { computed, signal } from '@preact/signals';
import type { Card, CardSummary, ProviderId, Settings } from '../types';
import { loadKeys, loadSettings, type ApiKeys } from '../storage/settings';
import { providerMeta } from '../ai/catalog';
import type { ModelOption } from '../ai/provider';
import type { EditorApi, EditorSelection } from '../editor/editor';

export interface StreamingState {
  cardId: string;
  messageId: string;
  controller: AbortController;
  /** When the request started, for the "Thinking" timer. */
  startedAt: number;
  /** Set while the model writes a proposal, so the UI can show which document is being written. */
  tool: { target: 'code' | 'spec'; lines: number } | null;
}

export type MobileTab = 'prompt' | 'code' | 'output';
export type WorkspaceTab = 'code' | 'spec';

export interface UiState {
  sidebarOpen: boolean;
  mobileTab: MobileTab;
  outputOpen: boolean;
  workspaceTab: WorkspaceTab;
  /** Fraction of the main area given to the conversation column. */
  splitRatio: number;
  /** Height of the output panel in pixels. */
  outputHeight: number;
  settingsOpen: boolean;
  inlineEditOpen: boolean;
}

const UI_KEY = 'cue.ui';

function loadUi(): UiState {
  const defaults: UiState = {
    sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 1100 : true,
    mobileTab: 'prompt',
    outputOpen: false,
    workspaceTab: 'code',
    splitRatio: 0.42,
    outputHeight: 220,
    settingsOpen: false,
    inlineEditOpen: false,
  };
  try {
    const raw = window.localStorage.getItem(UI_KEY);
    if (!raw) return defaults;
    const saved = JSON.parse(raw) as Partial<UiState>;
    return {
      ...defaults,
      // The saved choice is a desktop preference; where the list is an overlay, start with it closed.
      sidebarOpen: window.innerWidth >= 1100 ? (saved.sidebarOpen ?? defaults.sidebarOpen) : false,
      splitRatio: saved.splitRatio ?? defaults.splitRatio,
      outputHeight: saved.outputHeight ?? defaults.outputHeight,
      outputOpen: saved.outputOpen ?? defaults.outputOpen,
      workspaceTab: saved.workspaceTab === 'spec' ? 'spec' : 'code',
    };
  } catch {
    return defaults;
  }
}

export const settings = signal<Settings>(loadSettings());
export const apiKeys = signal<ApiKeys>(loadKeys());
export const cardList = signal<CardSummary[]>([]);
export const activeCard = signal<Card | null>(null);
/** Editor contents of the active card. Kept apart from `activeCard` so typing does not re-render the conversation. */
export const activeCode = signal<string>('');
/** Spec of the active card, kept apart for the same reason. */
export const activeSpec = signal<string>('');
export const streaming = signal<StreamingState | null>(null);
export const ui = signal<UiState>(loadUi());
export const toast = signal<{ id: number; text: string; kind: 'info' | 'error' } | null>(null);
export const modelOptions = signal<Partial<Record<ProviderId, ModelOption[]>>>({});
export const modelListStatus = signal<'idle' | 'loading' | 'error'>('idle');
/** Review progress in one document (the code or the spec). */
export interface DocReview {
  /** Changes still undecided. */
  remaining: number;
  /** The document was empty, so there is nothing to diff against. */
  newFile: boolean;
}

export interface ReviewInfo {
  proposalId: string | null;
  /** The model is still writing it. */
  streaming: boolean;
  /** Null when the proposal leaves that document as it is. */
  code: DocReview | null;
  spec: DocReview | null;
}

/** Active review (a proposal waiting for a decision, or one still being written). */
export const review = signal<ReviewInfo | null>(null);
export const editorSelection = signal<EditorSelection | null>(null);
export const legacyImportCount = signal<number>(0);

/** What the next prompt carries along. Reset when the card changes; output turns on after a failed run. */
export const attach = signal<{ code: boolean; spec: boolean; output: boolean }>({ code: true, spec: true, output: false });

/** Suggested next prompts for a card, from the model. */
export const suggestions = signal<{ cardId: string; items: string[]; status: 'loading' | 'ready' } | null>(null);

/** The editors are imperative; actions reach them through these bridges once the workspace has mounted. */
export const editorBridge: { current: EditorApi | null } = { current: null };
export const specBridge: { current: EditorApi | null } = { current: null };

export const currentModel = computed(
  () => settings.value.models[settings.value.provider] ?? providerMeta(settings.value.provider).defaultModel,
);

/** A readable name for a model id, from the loaded list or the built-in one, else the id without its vendor. */
export function modelLabel(provider: ProviderId, id: string): string {
  const known = [...(modelOptions.value[provider] ?? []), ...providerMeta(provider).staticModels].find((m) => m.id === id || m.id === `${id}:free`);
  return known?.label ?? id.slice(id.lastIndexOf('/') + 1);
}

/** Free to call: OpenRouter's `:free` variants and its free router. */
export const isFreeModel = (id: string) => id.endsWith(':free') || id === 'openrouter/free';

export const currentApiKey = computed(() => apiKeys.value[settings.value.provider] ?? '');

export const providerReady = computed(() => {
  const meta = providerMeta(settings.value.provider);
  return !meta.needsKey || currentApiKey.value.trim() !== '';
});

export const isDark = signal<boolean>(false);

export function updateUi(patch: Partial<UiState>): void {
  ui.value = { ...ui.value, ...patch };
  try {
    const { splitRatio, outputHeight, outputOpen, workspaceTab } = ui.value;
    const previous = JSON.parse(window.localStorage.getItem(UI_KEY) ?? '{}') as Partial<UiState>;
    // The saved card-list state is a desktop preference: only an explicit toggle at desktop width changes it.
    // Otherwise a phone-width session (where the list starts closed) would overwrite it.
    const desktopToggle = 'sidebarOpen' in patch && window.innerWidth >= 1100;
    const sidebarOpen = desktopToggle ? ui.value.sidebarOpen : previous.sidebarOpen;
    window.localStorage.setItem(UI_KEY, JSON.stringify({ sidebarOpen, splitRatio, outputHeight, outputOpen, workspaceTab }));
  } catch {
    // storage unavailable
  }
}

let toastSequence = 0;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

export function showToast(text: string, kind: 'info' | 'error' = 'info'): void {
  const id = ++toastSequence;
  toast.value = { id, text, kind };
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    if (toast.value?.id === id) toast.value = null;
  }, kind === 'error' ? 6000 : 3500);
}
